"use client";
import { createWalletClient, custom, type WalletClient, type EIP1193Provider } from "viem";

declare global {
  interface Window {
    ethereum?: EIP1193Provider;
  }
}

const CHAIN_ID = Number(process.env.NEXT_PUBLIC_CHAIN_ID ?? 31337);
const CHAIN_ID_HEX = `0x${CHAIN_ID.toString(16)}`;
const RPC_URL = process.env.NEXT_PUBLIC_CHAIN_RPC_URL ?? "http://127.0.0.1:8546";

export function hasInjectedWallet(): boolean {
  return typeof window !== "undefined" && !!window.ethereum;
}

function getInjectedWalletClient(): WalletClient {
  if (!window.ethereum) throw new Error("MetaMask 등 EVM 지갑 확장 프로그램을 찾을 수 없습니다.");
  return createWalletClient({ transport: custom(window.ethereum) });
}

export async function ensureLocalChain(): Promise<void> {
  if (!window.ethereum) return;
  try {
    await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: CHAIN_ID_HEX }] });
  } catch (err) {
    const code = (err as { code?: number } | undefined)?.code;
    if (code === 4902) {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: CHAIN_ID_HEX,
            chainName: "ARTPASS Local Chain",
            rpcUrls: [RPC_URL],
            nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
          },
        ],
      });
    } else {
      throw err;
    }
  }
}

export async function requestAccount(): Promise<`0x${string}`> {
  const client = getInjectedWalletClient();
  const [address] = await client.requestAddresses();
  await ensureLocalChain();
  return address;
}

export async function signMessageWithWallet(address: `0x${string}`, message: string): Promise<`0x${string}`> {
  const client = getInjectedWalletClient();
  return client.signMessage({ account: address, message });
}
